import type {
  QuestionResponseDetails,
  SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';
import { normalizeToTimestamp } from 'src/utils/dateUtils';

/**
 * "Who last touched this response, and when." Extracted from a
 * `QuestionResponseDetails` (parent) or a `SubQuestionResponseEntry`
 * (sub-question row) into a small shape a UI component can consume
 * without knowing about SDK types.
 *
 * Only produced when the response is a REAL save — never-answered
 * responses are represented in the summary cache as
 * `respondedBy: '' / respondedAt: new Date(0)` (see
 * `patchAuditCachePlumbing.ts` EMPTY_RESPONSE seed), and callers
 * should render nothing for those. The extractors below return
 * `null` in that case.
 */
export interface ResponseAuthorship {
  /** Canonical Amazon alias of the auditor who last saved. */
  readonly login: string;
  /** Timestamp of that save. Callers should pass through to a
   *  formatter — do not reformat inline. */
  readonly updatedAt: Date;
}

/**
 * Return authorship if `response` represents a real save, else
 * `null`. A response counts as "real" when it carries a non-empty
 * `respondedBy` AND a `respondedAt` past epoch — the cache plumbing
 * seeds `respondedBy: '' / respondedAt: new Date(0)` for
 * never-answered questions and we don't want to show
 * "updated by [empty], Jan 1 1970" on those.
 *
 * Both public extractors below delegate to this one. Exported so
 * callers that already hold a specific `QuestionResponseDetails`
 * slice (e.g. a sub-question row that receives its own
 * `SubQuestionResponseEntry`) don't need to re-search the parent's
 * `subResponses` array via {@link getSubResponseAuthorship}.
 */
export function getResponseAuthorship(
  response: QuestionResponseDetails | undefined,
): ResponseAuthorship | null {
  if (!response) return null;
  const { respondedBy, respondedAt } = response;
  if (!respondedBy) return null;
  // Wire-shape drift is real here: the SDK types respondedAt as
  // `Date`, the optimistic cache patch writes a `Date`, but the
  // summary endpoint has been observed handing sub-responses back
  // as ISO strings, epoch-millis numbers, and epoch-millis strings
  // depending on the read path (OpenSearch projection vs DDB
  // consistency read; see comments in `useUpdateAuditQuestionResponseMutation`
  // about Smithy's RFC-3339 deserializer mismatch). Delegate to
  // `normalizeToTimestamp` — the canonical helper already handles
  // all four wire shapes AND rejects the epoch-zero seed by
  // returning 0. A bespoke `Number(x)` coercion would silently
  // convert an ISO string to NaN and hide the badge on refresh.
  const time = normalizeToTimestamp(respondedAt);
  // `normalizeToTimestamp` returns NaN for a NaN number input
  // (its seconds-vs-millis heuristic short-circuits before the
  // isFinite check). Re-guard with Number.isFinite AND the
  // positivity check together so both the NaN wire shape and the
  // epoch-zero seed shape produce a null → no badge.
  if (!Number.isFinite(time) || time <= 0) return null;
  return { login: respondedBy, updatedAt: new Date(time) };
}

/**
 * Extract authorship for the PARENT question's response. Returns
 * `null` for undefined input or for a never-answered seed.
 */
export function getParentResponseAuthorship(
  response: QuestionResponseDetails | undefined,
): ResponseAuthorship | null {
  return getResponseAuthorship(response);
}

/**
 * Extract authorship for a specific SUB-question response, looked
 * up by `subQuestionId` inside the parent's `subQuestionResponses`
 * array. Returns `null` when the array is undefined/empty, when no
 * entry matches the id, or when the matched entry's response is a
 * never-answered seed.
 */
export function getSubResponseAuthorship(
  subResponses: readonly SubQuestionResponseEntry[] | undefined,
  subQuestionId: string,
): ResponseAuthorship | null {
  if (!subResponses || subResponses.length === 0) return null;
  const entry = subResponses.find((sub) => sub.subQuestionId === subQuestionId);
  return getResponseAuthorship(entry?.response);
}
